import { CSSProperties, FC, HTMLAttributes, ReactNode, useId, useState } from 'react';
import { Button, Typography } from 'antd';
import { TbChevronRight } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';

export interface CompactCollapsibleProps {
    bordered?: boolean;
    children: ReactNode;
    contentStyle?: CSSProperties;
    description?: ReactNode;
    descriptionStyle?: CSSProperties;
    disabled?: boolean;
    destroyOnHidden?: boolean;
    edgeToEdge?: boolean;
    headerDataAttributes?: Record<`data-${string}`, string | undefined>;
    headerExtra?: ReactNode;
    headerPrefix?: ReactNode;
    headerPrefixHoverOnly?: boolean;
    headerProps?: Omit<HTMLAttributes<HTMLDivElement>, 'children' | 'style'>;
    headerSelectionActive?: boolean;
    headerSelection?: ReactNode;
    headerStyle?: CSSProperties;
    icon?: ReactNode;
    id?: string;
    label: ReactNode;
    open: boolean;
    sectionStyle?: CSSProperties;
    sectionProps?: Omit<HTMLAttributes<HTMLElement>, 'children' | 'id' | 'style'>;
    onOpenChange: (open: boolean) => void;
}

const CompactCollapsible: FC<CompactCollapsibleProps> = ({
    bordered = false,
    children,
    contentStyle,
    description,
    descriptionStyle,
    disabled = false,
    destroyOnHidden = false,
    edgeToEdge = false,
    headerDataAttributes,
    headerExtra,
    headerPrefix,
    headerPrefixHoverOnly = false,
    headerProps,
    headerSelectionActive = false,
    headerSelection,
    headerStyle,
    icon,
    id,
    label,
    open,
    sectionStyle,
    sectionProps,
    onOpenChange,
}): JSX.Element => {
    const { token } = useAntDesignTheme();
    const generatedId = useId();
    const contentId = `${id ?? generatedId}-content`;
    const expanded = open && !disabled;
    const [retainCollapsedContent, setRetainCollapsedContent] = useState(expanded);
    const [headerHovered, setHeaderHovered] = useState(false);
    const shouldRenderContent = !destroyOnHidden || expanded || retainCollapsedContent;
    const showHeaderPrefix = !headerPrefixHoverOnly || headerHovered;
    const transition = `grid-template-rows ${token.motionDurationMid} ${token.motionEaseInOut}, opacity ${token.motionDurationMid} ${token.motionEaseInOut}, visibility 0s linear ${expanded ? '0s' : token.motionDurationMid}`;

    return (
        <section
            {...sectionProps}
            id={id}
            style={{
                ...(bordered
                    ? { border: `1px solid ${token.colorBorder}` }
                    : { borderTop: `1px solid ${token.colorBorder}` }),
                borderRadius: bordered ? token.borderRadius : undefined,
                ...(edgeToEdge
                    ? {
                          borderLeft: 0,
                          borderRadius: 0,
                          borderRight: 0,
                          borderTop: 0,
                          margin: 0,
                      }
                    : undefined),
                opacity: disabled ? 0.55 : 1,
                overflow: bordered ? 'hidden' : undefined,
                scrollMarginTop: '0.75rem',
                ...sectionStyle,
            }}
        >
            <div
                {...headerDataAttributes}
                {...headerProps}
                style={{
                    alignItems: 'center',
                    display: 'flex',
                    minHeight: '2.75rem',
                    padding: '0 0.75rem',
                    ...headerStyle,
                }}
                onMouseEnter={(event) => {
                    setHeaderHovered(true);
                    headerProps?.onMouseEnter?.(event);
                }}
                onMouseLeave={(event) => {
                    setHeaderHovered(false);
                    headerProps?.onMouseLeave?.(event);
                }}
            >
                {headerSelection ? (
                    <span
                        data-compact-collapsible-selection-control='true'
                        style={{
                            alignItems: 'center',
                            display: 'inline-flex',
                            flex: `0 0 ${headerHovered || headerSelectionActive ? '1rem' : 0}`,
                            marginRight: headerHovered || headerSelectionActive ? '0.5rem' : 0,
                            opacity: headerHovered || headerSelectionActive ? 1 : 0,
                            overflow: 'hidden',
                            pointerEvents: headerHovered || headerSelectionActive ? 'auto' : 'none',
                            transition:
                                'flex-basis 150ms ease, margin-right 150ms ease, opacity 150ms ease, width 150ms ease',
                            width: headerHovered || headerSelectionActive ? '1rem' : 0,
                        }}
                    >
                        {headerSelection}
                    </span>
                ) : null}
                <Button
                    aria-controls={contentId}
                    aria-expanded={expanded}
                    aria-label={`${expanded ? 'Collapse' : 'Expand'} ${typeof label === 'string' ? label : 'section'}`}
                    disabled={disabled}
                    icon={
                        <TbChevronRight
                            style={{
                                color: token.colorTextTertiary,
                                transform: expanded ? 'rotate(90deg)' : undefined,
                                transition: `transform ${token.motionDurationMid} ${token.motionEaseInOut}`,
                            }}
                        />
                    }
                    size='small'
                    type='text'
                    onClick={() => {
                        if (!expanded && destroyOnHidden) {
                            setRetainCollapsedContent(true);
                        }

                        onOpenChange(!expanded);
                    }}
                />
                {headerPrefix && (
                    <span
                        style={{
                            display: 'inline-flex',
                            marginLeft: showHeaderPrefix ? '0.5rem' : 0,
                            opacity: showHeaderPrefix ? 1 : 0,
                            overflow: 'hidden',
                            pointerEvents: showHeaderPrefix ? 'auto' : 'none',
                            transition: 'margin-left 150ms ease, opacity 150ms ease, width 150ms ease',
                            width: showHeaderPrefix ? '1.5rem' : 0,
                        }}
                    >
                        {headerPrefix}
                    </span>
                )}
                <div
                    style={{
                        alignItems: 'center',
                        color: disabled ? token.colorTextTertiary : token.colorText,
                        display: 'flex',
                        flex: '1 1 auto',
                        fontWeight: 500,
                        gap: '0.375rem',
                        marginLeft: '0.5rem',
                        minWidth: 0,
                    }}
                >
                    {icon && (
                        <span
                            aria-hidden
                            style={{
                                alignItems: 'center',
                                color: token.colorTextTertiary,
                                display: 'inline-flex',
                                flex: 'none',
                            }}
                        >
                            {icon}
                        </span>
                    )}
                    <span style={{ minWidth: 0 }}>{label}</span>
                </div>
                {headerExtra && <div style={{ flex: 'none', marginLeft: '0.5rem' }}>{headerExtra}</div>}
            </div>
            <div
                id={contentId}
                aria-hidden={!expanded}
                style={{
                    display: 'grid',
                    gridTemplateRows: expanded ? '1fr' : '0fr',
                    opacity: expanded ? 1 : 0,
                    transition,
                    visibility: expanded ? 'visible' : 'hidden',
                }}
                onTransitionEnd={(event) => {
                    if (
                        event.currentTarget === event.target &&
                        event.propertyName === 'grid-template-rows' &&
                        destroyOnHidden &&
                        !expanded
                    ) {
                        setRetainCollapsedContent(false);
                    }
                }}
            >
                <div style={{ minHeight: 0, overflow: 'hidden' }}>
                    <div style={{ padding: '0.25rem 1rem 1rem', ...contentStyle }}>
                        {!disabled && shouldRenderContent && (
                            <>
                                {description && (
                                    <Typography.Paragraph
                                        style={{
                                            color: token.colorTextTertiary,
                                            marginBottom: '1rem',
                                            ...descriptionStyle,
                                        }}
                                    >
                                        {description}
                                    </Typography.Paragraph>
                                )}
                                {children}
                            </>
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
};

export default CompactCollapsible;
